import { Box, FormControl, Grid, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useTimeOffImpact } from "../../hooks/useTimeOffImpact";
import { INK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { MAX_TIME_OFF_SPAN_DAYS } from "../../utils/consts";
import { getStaffingDate, getStaffingDayStart, isAffectByDayStart, isFullStaffingDayPto } from "../../utils/dateUtils";
import { type DayOff, PtoTypes, TimeOffReasons } from "../../utils/models";
import { CharCounter } from "../StaffCommunication/util/CharCounter";
import { DateInput } from "../UIToolkit/Inputs/DateInput";
import { ShiftHoursDisplayAndPicker } from "../UIToolkit/Inputs/ShiftHoursDisplayAndPicker";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../UIToolkit/molecules/DateRangeSelector";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import { AffectedShiftsSection } from "./AffectedShiftsSection";
import { useAddDayOff } from "./hooks";

type AddDayOffDialogProps = {
	open: boolean;
	nurseId: number;
	onClose: () => void;
	onSave: (dayOff: DayOff) => void;
	warnLongPtoNotReached?: boolean;
	longPtoMinimumDaysAmount?: number;
	dayOff?: DayOff;
	initialStartDate?: DateTime;
	initialEndDate?: DateTime;
	initialHours?: number;
	zIndex?: number;
};

const MAX_HOURS = 24;
const MINUTE_STEP = 5;
const MINUTES_PER_HOUR = 60;
const DEFAULT_HOURS = 8;
const maxChars = 300;

const splitDecimalHours = (decimalHours: number): { hours: number; minutes: number } => {
	const totalMinutes = Math.round(decimalHours * MINUTES_PER_HOUR);
	const clamped = Math.max(0, Math.min(MAX_HOURS * MINUTES_PER_HOUR, totalMinutes));
	const hours = Math.floor(clamped / MINUTES_PER_HOUR);
	const minutes = Math.round((clamped % MINUTES_PER_HOUR) / MINUTE_STEP) * MINUTE_STEP;
	if (minutes === MINUTES_PER_HOUR) {
		return { hours: Math.min(MAX_HOURS, hours + 1), minutes: 0 };
	}
	return { hours, minutes };
};

const AddDayOffDialog: FC<AddDayOffDialogProps> = ({
	open,
	onClose,
	onSave,
	nurseId,
	warnLongPtoNotReached = false,
	longPtoMinimumDaysAmount = 0,
	dayOff,
	initialStartDate,
	initialEndDate,
	initialHours,
	zIndex,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();

	// Editing a partial PTO opens in exact mode with the real times, bypassing getInitialDates — the
	// full-days path would silently widen it to 24h on resave.
	const deptTz = chosenDepartment.timezone;
	const editingPartial = !!dayOff && !isFullStaffingDayPto(dayOff.dateFrom.setZone(deptTz), dayOff.dateTo.setZone(deptTz), chosenDepartment.dayStart);
	const [mode, setMode] = useState<"full" | "exact">(editingPartial ? "exact" : "full");
	const [exactDate, setExactDate] = useState<DateTime | null>(
		editingPartial && dayOff ? getStaffingDate(dayOff.dateFrom.setZone(deptTz), chosenDepartment.dayStart).startOf("day") : (initialStartDate ?? null),
	);
	// DEFAULT: the shared time picker (ShiftHoursDisplayAndPicker) requires concrete values, so new
	// exact PTOs seed a visible 8:00 AM - 4:00 PM window the user adjusts; only hour/minute are read.
	const [exactStartTime, setExactStartTime] = useState<DateTime>(
		editingPartial && dayOff ? dayOff.dateFrom.setZone(deptTz) : DateTime.now().setZone(deptTz).set({ hour: 8, minute: 0, second: 0, millisecond: 0 }),
	);
	const [exactEndTime, setExactEndTime] = useState<DateTime>(
		editingPartial && dayOff ? dayOff.dateTo.setZone(deptTz) : DateTime.now().setZone(deptTz).set({ hour: 16, minute: 0, second: 0, millisecond: 0 }),
	);

	const getInitialDates = useCallback(() => {
		if (!dayOff) {
			return {
				start: initialStartDate ?? null,
				end: initialEndDate ?? null,
			};
		}

		const start = getStaffingDate(dayOff.dateFrom, chosenDepartment.dayStart);
		const end = getStaffingDate(dayOff.dateTo.minus({ minutes: 1 }), chosenDepartment.dayStart);

		return { start, end };
	}, [dayOff, initialStartDate, initialEndDate, chosenDepartment.dayStart]);

	const initialDates = getInitialDates();
	const [startDate, setStartDate] = useState<DateTime | null>(initialDates.start);
	const [endDate, setEndDate] = useState<DateTime | null>(initialDates.end);
	const [selectedReason, setSelectedReason] = useState<PtoTypes>((dayOff?.reasons[0] as PtoTypes) ?? PtoTypes.Payed);
	const initialDuration = splitDecimalHours(dayOff?.hours ?? initialHours ?? DEFAULT_HOURS);
	const [selectedHours, setSelectedHours] = useState<number>(initialDuration.hours);
	const [selectedMinutes, setSelectedMinutes] = useState<number>(initialDuration.minutes);
	const [postShifts, setPostShifts] = useState<boolean>(dayOff?.post_shifts ?? false);
	const [selectedTimeOffReason, setSelectedTimeOffReason] = useState<TimeOffReasons>(
		dayOff?.notes
			? Object.values(TimeOffReasons)
					.map((val) => String(val))
					.includes(dayOff.notes)
				? (dayOff.notes as TimeOffReasons)
				: TimeOffReasons.Other
			: TimeOffReasons.TimeOff,
	);
	const [otherTimeOffReason, setOtherTimeOffReason] = useState<string>(selectedTimeOffReason === TimeOffReasons.Other && dayOff?.notes ? dayOff.notes : "");
	const { mutateAsync: addDayOff, isPending } = useAddDayOff(nurseId, onSave, dayOff);
	const handleTimeOffReasonChange = useCallback((reason: TimeOffReasons) => {
		setSelectedTimeOffReason(reason);
		if (reason !== TimeOffReasons.Other) {
			setOtherTimeOffReason("");
		}
	}, []);

	const disabledDueToLongPtoNotReached = useMemo<boolean>(
		() => mode === "full" && warnLongPtoNotReached && !!endDate && !!startDate && endDate.diff(startDate).as("day") < longPtoMinimumDaysAmount,
		[mode, warnLongPtoNotReached, endDate, startDate, longPtoMinimumDaysAmount],
	);

	// The calendar window the exact-times inputs resolve to (times before day_start belong to the
	// staffing day's tail), shown as a caption so overnight/next-day resolution is never a surprise.
	const resolvedExactWindow = useMemo<{ start: DateTime; end: DateTime } | null>(() => {
		if (mode !== "exact" || !exactDate) return null;
		const dateInTz = exactDate.setZone(deptTz, { keepLocalTime: true });
		const start = isAffectByDayStart(dateInTz, exactStartTime.hour, exactStartTime.minute, chosenDepartment.dayStart);
		let end = isAffectByDayStart(dateInTz, exactEndTime.hour, exactEndTime.minute, chosenDepartment.dayStart);
		if (end <= start) {
			end = end.plus({ days: 1 });
		}
		return { start, end };
	}, [mode, exactDate, exactStartTime, exactEndTime, deptTz, chosenDepartment.dayStart]);

	// The resolved calendar window the current inputs produce (mirrors the mutation's resolution),
	// used to preview which shifts the save will remove/trim/split. Null while inputs are invalid.
	const affectedWindow = useMemo<{ start: DateTime; end: DateTime } | null>(() => {
		if (mode === "exact") {
			// Equal times are ambiguous (0h vs 24h) and blocked by validation — no preview for them.
			if (!resolvedExactWindow || (exactStartTime.hour === exactEndTime.hour && exactStartTime.minute === exactEndTime.minute)) return null;
			return resolvedExactWindow;
		}
		if (!startDate || !endDate || startDate.startOf("day") > endDate.startOf("day")) return null;
		const startInTz = startDate.setZone(deptTz, { keepLocalTime: true });
		const endInTz = endDate.setZone(deptTz, { keepLocalTime: true });
		return {
			start: getStaffingDayStart(startInTz, chosenDepartment.dayStart),
			end: getStaffingDayStart(endInTz.plus({ days: 1 }), chosenDepartment.dayStart),
		};
	}, [mode, resolvedExactWindow, exactStartTime, exactEndTime, startDate, endDate, deptTz, chosenDepartment.dayStart]);

	// Server-owned preview of what the save would do. The overlap rules live only in the backend, so
	// nothing here re-derives them — this component only reads the classification back.
	const {
		affectedShifts,
		isLoading: isImpactLoading,
		isRefreshing: isImpactRefreshing,
		isError: isImpactError,
	} = useTimeOffImpact(chosenDepartment.department_id, nurseId, affectedWindow, open);

	// Only removed shifts can be posted — trimmed and split shifts keep the nurse on them — and the
	// backend posts only those still in the future.
	//
	// Null while the count is genuinely unknown: the hook reports an empty list both before the first
	// answer and on failure, and stating "(0)" there would be the same false negative the
	// affected-shifts error state exists to prevent.
	const postableShiftsCount = useMemo<number | null>(() => {
		if (isImpactLoading || isImpactError) return null;
		const now = DateTime.now();
		// A leftover segment of an earlier trim is removed without posting, so it must not be counted.
		return affectedShifts.filter((shift) => shift.overlap === "remove" && shift.startTime > now && !shift.parentShiftId).length;
	}, [affectedShifts, isImpactLoading, isImpactError]);

	// A partial window posts only when it removes an upcoming shift, so the switch appears only then.
	const showPostShifts = mode === "full" ? !!endDate && endDate.startOf("day") >= DateTime.now().startOf("day") : !!postableShiftsCount;

	const handleApplyDayOff = useCallback(async (): Promise<void> => {
		if (mode === "exact") {
			if (!exactDate || !resolvedExactWindow) return;

			await addDayOff({
				startDate: exactDate,
				endDate: exactDate,
				reason: selectedReason,
				hours: Number(resolvedExactWindow.end.diff(resolvedExactWindow.start).as("hours").toFixed(2)),
				// A switch left on in full-days mode must not post shifts from a partial window where it's hidden.
				postShifts: showPostShifts && postShifts,
				notes: selectedTimeOffReason === TimeOffReasons.Other ? otherTimeOffReason : selectedTimeOffReason,
				exact: { startTime: exactStartTime, endTime: exactEndTime },
			});
			onClose();
			return;
		}

		if (!startDate || !endDate) return;

		const hours = Number((selectedHours + selectedMinutes / MINUTES_PER_HOUR).toFixed(2));
		await addDayOff({
			startDate,
			endDate,
			reason: selectedReason,
			hours,
			postShifts,
			notes: selectedTimeOffReason === TimeOffReasons.Other ? otherTimeOffReason : selectedTimeOffReason,
		});
		onClose();
	}, [
		mode,
		exactDate,
		exactStartTime,
		exactEndTime,
		resolvedExactWindow,
		startDate,
		endDate,
		selectedReason,
		selectedHours,
		selectedMinutes,
		postShifts,
		showPostShifts,
		selectedTimeOffReason,
		addDayOff,
		onClose,
		otherTimeOffReason,
	]);

	const confirmDisabledReason = useMemo<string | null>(() => {
		if (mode === "exact") {
			if (!exactDate) {
				return t("start.date.required");
			}
			if (exactStartTime.hour === exactEndTime.hour && exactStartTime.minute === exactEndTime.minute) {
				return t("exact.times.equal");
			}
			if (isPending) {
				return t("saving");
			}
			if (selectedTimeOffReason === TimeOffReasons.Other && otherTimeOffReason.trim() === "") {
				return t("specify.time.off.reason.required");
			}
			return null;
		}
		if (!startDate) {
			return t("start.date.required");
		}
		if (!endDate) {
			return t("end.date.required");
		}
		if (isPending) {
			return t("saving");
		}
		if (startDate.startOf("day") > endDate.startOf("day")) {
			return t("start.date.after.end.date");
		}
		if (endDate.startOf("day").diff(startDate.startOf("day"), "days").days > MAX_TIME_OFF_SPAN_DAYS) {
			return t("pto.date.range.too.long");
		}
		if (selectedTimeOffReason === TimeOffReasons.Other && otherTimeOffReason.trim() === "") {
			return t("specify.time.off.reason.required");
		}
		return null;
	}, [mode, exactDate, exactStartTime, exactEndTime, startDate, endDate, isPending, selectedTimeOffReason, otherTimeOffReason, t]);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={dayOff ? t("edit.time.off") : t("time.off")}
			dataTestId="add-day-off-dialog"
			zIndex={zIndex}
			body={
				<Grid
					container
					spacing={2}
					sx={{
						pt: 1,
					}}
				>
					<Grid size={12}>
						<ToggleButtonGroup
							value={mode}
							exclusive
							onChange={(_, newMode) => newMode !== null && setMode(newMode)}
							fullWidth
							size="small"
							data-testid="add-day-off-mode-toggle"
						>
							{(["full", "exact"] as const).map((modeOption) => (
								<ToggleButton
									key={modeOption}
									value={modeOption}
									data-testid={`add-day-off-mode-${modeOption}`}
									sx={(theme) => ({
										textTransform: "none",
										fontWeight: 600,
										fontSize: "14px",
										color: mode === modeOption ? SURFACE : INK.SECONDARY,
										bgcolor: mode === modeOption ? theme.palette.primary.main : SURFACE,
										border: `1px solid ${NEUTRAL[300]}`,
										"&:hover": {
											bgcolor: mode === modeOption ? theme.palette.primary.main : NEUTRAL[100],
										},
										"&.Mui-selected": {
											bgcolor: theme.palette.primary.main,
											color: SURFACE,
											"&:hover": { bgcolor: theme.palette.primary.dark },
										},
									})}
								>
									{modeOption === "full" ? t("full.days") : t("exact.times")}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Grid>
					{mode === "full" ? (
						<Grid size={12}>
							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<DateRangeSelector
									padding={0}
									startDate={startDate ?? undefined}
									endDate={endDate ?? undefined}
									endMaxDate={startDate?.plus({ days: MAX_TIME_OFF_SPAN_DAYS })}
									onStartDateChange={(date) => {
										setStartDate(date);
									}}
									onEndDateChange={(date) => {
										setEndDate(date);
									}}
									removeBackground
									removeBorder
									containerSX={{ width: "100%" }}
									dateInputSX={{ flex: 1, minWidth: 0 }}
								/>
							</LocalizationProvider>
						</Grid>
					) : (
						<Grid size={12}>
							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<Grid container spacing={1}>
									<Grid size={12}>
										<DateInput
											value={exactDate ?? undefined}
											onChange={(date) => setExactDate(date)}
											label={t("date")}
											dataTestId="add-day-off-exact-date-input"
											formControlSx={{ width: "100%" }}
										/>
									</Grid>
									<Grid size={12} data-testid="add-day-off-exact-times-picker">
										<ShiftHoursDisplayAndPicker
											selectedRoleForShift={undefined}
											roleSource="nurse"
											shiftStart={exactStartTime}
											shiftEnd={exactEndTime}
											setShiftStart={setExactStartTime}
											setShiftEnd={setExactEndTime}
											enabled
										/>
									</Grid>
									{resolvedExactWindow && (
										<Grid size={12}>
											<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "13px" }} data-testid="add-day-off-exact-window-caption">
												{`${resolvedExactWindow.start.toFormat("ccc, MMM d, h:mm a")} - ${resolvedExactWindow.end.toFormat(
													resolvedExactWindow.start.hasSame(resolvedExactWindow.end, "day") ? "h:mm a" : "ccc, MMM d, h:mm a",
												)}`}
											</Typography>
										</Grid>
									)}
								</Grid>
							</LocalizationProvider>
						</Grid>
					)}
					{affectedWindow && (
						<Grid size={12}>
							<AffectedShiftsSection
								shifts={affectedShifts}
								windowStart={affectedWindow.start}
								windowEnd={affectedWindow.end}
								isEdit={!!dayOff}
								isLoading={isImpactLoading}
								isRefreshing={isImpactRefreshing}
								isError={isImpactError}
							/>
						</Grid>
					)}
					<Grid size={12}>
						<FormControl fullWidth>
							<Typography
								sx={{
									color: alpha(INK.SECONDARY, 0.5),
									fontSize: "13px",
									fontWeight: 600,
								}}
							>
								{t("type")}
							</Typography>
							<ToggleButtonGroup
								value={selectedReason}
								exclusive
								onChange={(_, newValue) => newValue !== null && setSelectedReason(newValue)}
								fullWidth
								data-testid="add-day-off-type-toggle"
							>
								{Object.entries(PtoTypes).map(([key, value]) => (
									<ToggleButton
										key={key}
										value={value}
										data-testid={`add-day-off-type-${key.toLowerCase()}`}
										sx={(theme) => ({
											textTransform: "none",
											fontWeight: 600,
											fontSize: "14px",
											color: selectedReason === value ? SURFACE : INK.SECONDARY,
											bgcolor: selectedReason === value ? theme.palette.primary.main : SURFACE,
											border: `1px solid ${NEUTRAL[300]}`,
											"&:hover": {
												bgcolor: selectedReason === value ? theme.palette.primary.main : NEUTRAL[100],
											},
											"&.Mui-selected": {
												bgcolor: theme.palette.primary.main,
												color: SURFACE,
												"&:hover": { bgcolor: theme.palette.primary.dark },
											},
										})}
									>
										{value.replace(" time off", "").replace(" timeoff", "")}
									</ToggleButton>
								))}
							</ToggleButtonGroup>
						</FormControl>
					</Grid>
					{mode === "full" && (
						<Grid size={12}>
							<Typography
								sx={{
									color: alpha(INK.SECONDARY, 0.5),
									fontSize: "13px",
									fontWeight: 600,
								}}
							>
								{t("hours.per.day")}
							</Typography>
							<Grid container spacing={1}>
								<Grid size={6}>
									<SystemDropdown
										items={Array.from({ length: MAX_HOURS + 1 }, (_, i) => ({ value: i, label: `${i} ${t("hours")}` }))}
										selectedItem={selectedHours}
										onChange={(v) => {
											if (v === undefined) return;
											setSelectedHours(v);
											if (v === MAX_HOURS) setSelectedMinutes(0);
										}}
										fullWidth
										data-testid="add-day-off-hours-select"
									/>
								</Grid>
								<Grid size={6}>
									<SystemDropdown
										items={Array.from({ length: MINUTES_PER_HOUR / MINUTE_STEP }, (_, i) => ({
											value: i * MINUTE_STEP,
											label: `${i * MINUTE_STEP} ${t("minutes")}`,
										}))}
										selectedItem={selectedMinutes}
										onChange={(v) => {
											if (v !== undefined) setSelectedMinutes(v);
										}}
										disabled={selectedHours === MAX_HOURS}
										fullWidth
										data-testid="add-day-off-minutes-select"
									/>
								</Grid>
							</Grid>
						</Grid>
					)}
					<Grid size={12}>
						<Typography
							sx={{
								color: alpha(INK.SECONDARY, 0.5),
								fontSize: "13px",
								fontWeight: 600,
							}}
						>
							{t("reason")}
						</Typography>
						<SystemDropdown
							items={Object.values(TimeOffReasons).map((value) => ({ value, label: value }))}
							selectedItem={selectedTimeOffReason}
							onChange={(v) => {
								if (v !== undefined) handleTimeOffReasonChange(v);
							}}
							fullWidth
							data-testid="add-day-off-reason-select"
						/>
					</Grid>
					{selectedTimeOffReason === TimeOffReasons.Other && (
						<Grid size={12}>
							<SystemValidationTextField
								reserveHeightForHelperText={false}
								dataTestId="add-day-off-other-reason-input"
								injectedProps={{
									multiline: true,
									rows: 5,
									slotProps: {
										htmlInput: {
											maxLength: maxChars,
											style: {
												scrollbarWidth: "thin",
												scrollbarColor: `${NEUTRAL[500]} transparent`,
											},
										},
									},
								}}
								formControlSx={{
									width: "100%",
								}}
								value={otherTimeOffReason}
								onChange={(event) => setOtherTimeOffReason(event.target.value)}
								placeholder={t("specify.time.off.reason", { maxChars })}
							/>
							<CharCounter text={otherTimeOffReason} maxChars={maxChars} render={"always"} />
						</Grid>
					)}
					<Grid size={12}>
						{showPostShifts && (
							<FormControl fullWidth>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<Switch
										checked={postShifts}
										onChange={({ target }) => setPostShifts(target.checked)}
										disabled={mode === "full" && (!startDate || !endDate)}
										data-testid="add-day-off-post-shifts-switch"
										sx={(theme) => ({
											color: mode === "full" && (!startDate || !endDate) ? INK.DISABLED : theme.palette.primary.main,
										})}
									/>
									<Typography
										sx={(theme) => ({
											color: postShifts ? theme.palette.primary.main : INK.DISABLED,
											fontSize: "16px",
											fontWeight: 400,
										})}
									>
										{postableShiftsCount === null ? t("post.new.shifts.unknown") : t("post.new.shifts.count", { count: postableShiftsCount })}
									</Typography>
								</Box>
							</FormControl>
						)}
						{disabledDueToLongPtoNotReached && <Typography sx={{ color: INK.SECONDARY }}>{t("time.off.saved.profile")}</Typography>}
					</Grid>
				</Grid>
			}
			cancel={t("cancel")}
			confirm={t("save")}
			onConfirm={handleApplyDayOff}
			confirmTooltip={confirmDisabledReason ?? undefined}
			confirmDisabled={!!confirmDisabledReason}
		/>
	);
};

export default AddDayOffDialog;
